import { useState } from "react";
import { SECTION_HEADING_CLASS } from "bizpandit-ui";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Banner, Card, CardContent, Spinner, toast } from "bizpandit-ui";
import { ROUTES } from "@/app/constants";
import { useAuthStore } from "@/stores/authStore";
import { accountSettingsService } from "../services/accountSettings.service";
import { apiErrorMessage } from "@/api/apiError";

/**
 * Permanent erasure — the client's own door.
 *
 * Reachable only from the body-text link on Account Settings. Not in the
 * navigation, not in search, not linked from the dashboard.
 *
 * All three gates must be cleared before the confirm button enables, and every
 * one of them is re-checked server-side — the disabled button is a courtesy,
 * not the control. The friction is the whole point: the purge is immediate and
 * has no undo, so this is the only protection against a decision made in five
 * bad minutes.
 */
export default function ClientEraseDataPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [password, setPassword] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [code, setCode] = useState("");
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);

  const { data: eligibility, isLoading } = useQuery({
    queryKey: ["account-erasure-eligibility"],
    queryFn: () => accountSettingsService.eligibility(),
  });

  const start = useMutation({
    mutationFn: () => accountSettingsService.startErase(),
    onSuccess: (r) => {
      setMaskedPhone(r.maskedPhone);
      toast({ title: "Code sent", description: "Check WhatsApp on your registered mobile." });
    },
    onError: (e: unknown) => {
      toast({ title: "Could not send the code", description: apiErrorMessage(e, "Unexpected error") });
    },
  });

  const erase = useMutation({
    mutationFn: () =>
      accountSettingsService.erase({ password, confirmEmail: typedEmail, code }),
    onSuccess: () => {
      // No toast: there is no session left to show it in, and the page they
      // came from no longer loads.
      window.location.href = `${ROUTES.login}?erased=1`;
    },
    onError: (e: unknown) => {
      toast({ title: "Could not erase your data", description: apiErrorMessage(e, "Unexpected error") });
    },
  });

  // A7: a person with financial records keeps their identity on those records.
  const retained = !!eligibility?.retention?.applies;

  const emailMatches =
    !!user?.email && typedEmail.trim().toLowerCase() === user.email.trim().toLowerCase();
  const allGatesClear = password.length > 0 && emailMatches && code.length >= 4;

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  // Money or work outstanding. The client is told to contact support rather
  // than shown the raw list — which orders are open is not their diagnostic.
  if (eligibility && !eligibility.canErase) {
    return (
      <div className="p-5 max-w-[640px]">
        <h1 className="m-0 text-[22px] font-semibold text-navy">
          Permanently erase my personal data
        </h1>
        <Card className="border border-linegray rounded-[14px] mt-4">
          <CardContent className="p-4">
            <p className="text-[13px] text-navy2 m-0 leading-relaxed">
              Your account has outstanding items, so it cannot be erased right
              now. Please contact support and we will sort it out with you.
            </p>
          </CardContent>
        </Card>
        <button
          type="button"
          onClick={() => navigate(ROUTES.portalAccountSettings)}
          className="border border-linegray bg-white text-navy2 px-3.5 py-2 rounded-[10px] text-[13px] hover:bg-surface transition-colors mt-4"
        >
          Take me back
        </button>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-[720px]">
      <div className="text-[12px] text-slate-500 mb-1">Account Settings › Erase my data</div>
      <h1 className="m-0 text-[22px] font-semibold text-navy">
        Permanently erase my personal data
      </h1>
      <p className="text-slate-500 text-[13px] mt-1 mb-5">
        Please read this carefully. There is no undo.
      </p>

      {retained && eligibility?.retention?.notice && (
        <div className="mb-5">
          <Banner tone="warning" title="Because you have made payments">{eligibility.retention.notice}</Banner>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="bg-rosepale border border-roselight rounded-[10px] p-3.5">
          <div className="text-[11px] uppercase tracking-wider text-roseink font-bold mb-2">
            Erased forever
          </div>
          <ul className="m-0 pl-4 text-[13px] text-navy2 space-y-1">
            {!retained && <li>Your name, email, phone and address</li>}
            <li>Your birth date, time and place</li>
            <li>Every horoscope and dasha calculation</li>
            <li>Every BizCalendar and its daily notes</li>
            <li>Your remedies and reports</li>
            <li>Any share links you created</li>
          </ul>
        </div>
        <div className="bg-surface border border-linegray rounded-[10px] p-3.5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
            Kept, by law
          </div>
          <ul className="m-0 pl-4 text-[13px] text-navy2 space-y-1">
            <li>Invoices and receipts</li>
            <li>Payments</li>
            <li>The record that a service was delivered</li>
          </ul>
          <p className="text-[12px] text-slate-500 mt-2 mb-0">
            {retained
              ? "Your name and the contact details on these records stay until the statutory period ends, then they are erased too."
              : "These no longer carry your name."}
          </p>
        </div>
      </div>

      <Card className="border border-linegray rounded-[14px]">
        <CardContent className="p-4">
          <h2 className={`m-0 mb-4 ${SECTION_HEADING_CLASS}`}>
            To continue, complete all three steps
          </h2>

          <label className="block text-[12px] font-semibold text-navy2 mb-1">
            1. Your password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-linegray rounded-[10px] px-3 py-2 text-sm mb-4"
          />

          <label className="block text-[12px] font-semibold text-navy2 mb-1">
            2. Type your email exactly
          </label>
          <div className="text-[12px] text-slate-500 mb-1">{user?.email ?? "—"}</div>
          <input
            type="text"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            autoComplete="off"
            className={`w-full border rounded-[10px] px-3 py-2 text-sm mb-4 ${
              typedEmail && !emailMatches ? "border-red-400" : "border-linegray"
            }`}
          />

          <label className="block text-[12px] font-semibold text-navy2 mb-1">
            3. Code sent to your mobile
          </label>
          <div className="flex gap-2 items-start mb-1">
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit code"
              className="flex-1 border border-linegray rounded-[10px] px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={start.isPending}
              onClick={() => start.mutate()}
              className="border border-linegray bg-white text-navy2 px-3 py-2 rounded-[10px] text-[13px] hover:bg-surface transition-colors whitespace-nowrap"
            >
              {start.isPending ? "Sending…" : "Send code"}
            </button>
          </div>
          {maskedPhone && (
            <div className="text-[12px] text-slate-500 mb-4">Sent to {maskedPhone}</div>
          )}

          <p className="text-[13px] text-navy2 mt-5 mb-3 font-medium">
            Once you press the button below, your data is gone immediately.
          </p>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(ROUTES.portalAccountSettings)}
              className="border border-linegray bg-white text-navy2 px-3.5 py-2 rounded-[10px] text-[13px] hover:bg-surface transition-colors"
            >
              Take me back
            </button>
            <button
              type="button"
              disabled={!allGatesClear || erase.isPending}
              onClick={() => erase.mutate()}
              className={`px-3.5 py-2 rounded-[10px] text-[13px] font-medium transition-colors
                ${!allGatesClear || erase.isPending
                  ? "bg-surface text-slate-400 cursor-not-allowed border border-linegray"
                  : "bg-rose text-roseink hover:bg-rosedark hover:text-white"}`}
            >
              {erase.isPending ? <Spinner /> : "Erase my data permanently"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
